import React from 'react'
import { Tab, Tabs } from '@fs/zion-ui'
import { getLanguage } from '@fs/zion-standards'
import { useTranslation } from 'react-i18next'
import { langText } from '@fs/zion-locale'
import PlaceReportsMarkdown from './PlaceReportsMarkdown'
import PlaceReportsNoRegionSelectedMap from './PlaceReportsNoRegionSelectedMap'
import useMarkdownContent from '../../hooks/useMarkdownContent'
import ContentWrapper from '../../ContentWrapper'
import PlaceContentsNotAvailable from './PlaceContentsNotAvailable'
import { usePlaceReportsData } from '../../PlaceReportsProvider'

function getLanguageTitle(locale: string): string {
  return (
    langText.core.find((l) => l.code === locale)?.text ||
    langText.additional.find((l) => l.code === locale)?.text ||
    getLanguage(locale)?.primaryText ||
    locale
  )
}

export default function PlaceReportsContentPanel(): JSX.Element | null {
  const [t] = useTranslation()
  const { viewType } = usePlaceReportsData()
  const {
    markdown,
    locales,
    selectedLocale,
    setSelectedLocale,
    fetchMarkdown,
    getMarkdownPath,
    error,
    loading,
    region,
  } = useMarkdownContent()

  if (loading) {
    return null
  }

  if (error) {
    const headingKey =
      viewType === 'scorecard'
        ? 'place-reports.content-panel.scorecard-pending.header'
        : 'place-reports.content-panel.content-pending.header'
    const headingDefault = viewType === 'scorecard' ? 'Scorecard Pending' : 'Guidelines Pending'

    return (
      <ContentWrapper>
        <PlaceContentsNotAvailable
          heading={t(headingKey, headingDefault)}
          body={t(
            'place-reports.content-panel.content-pending.message',
            'There are not yet any specific guidelines for this place. Information is updated regularly, so please check back again, or contact the Authorities team or your local FamilySearch Missionaries for more urgent inquiries.'
          )}
        />
      </ContentWrapper>
    )
  }

  const showMarkdown = region && markdown && markdown.length > 0
  const selected = locales?.indexOf(selectedLocale) || 0

  return (
    <ContentWrapper>
      {Boolean(locales?.length && locales.length > 1) && (
        <Tabs
          index={selected}
          onChange={(ind) => {
            const newLocale = locales[ind]
            setSelectedLocale(newLocale)
            const markdownPath = getMarkdownPath(newLocale)
            if (markdownPath) {
              fetchMarkdown(markdownPath)
            }
          }}
        >
          {locales.map((locale) => {
            const title = getLanguageTitle(locale)
            return (
              <Tab key={locale} title={title}>
                {showMarkdown && <PlaceReportsMarkdown markdown={markdown} />}
              </Tab>
            )
          })}
        </Tabs>
      )}
      {showMarkdown && locales.length === 1 && <PlaceReportsMarkdown markdown={markdown} />}
      {!showMarkdown && (
        <PlaceReportsNoRegionSelectedMap
          message={t('place-reports-content-panel.select-a-region.message', 'Select a Region')}
        />
      )}
    </ContentWrapper>
  )
}
